import React, { createContext, useContext, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, Link } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Navbar from './components/Navbar';
import Dashboard from './pages/Dashboard';
import ProjectDetail from './pages/ProjectDetail';
import Settings from './pages/Settings';
import Landing from './pages/Landing';
import Login from './pages/Login';
import ExtLogin from './pages/ExtLogin';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import ScopeAnalysis from './pages/ScopeAnalysis';
import { getProfiles } from './api';

const ToastContext = createContext();

export function useToast() {
  return useContext(ToastContext);
}

class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  render() {
    if (this.state.hasError) return (
      <div style={{ padding: 20, color: 'var(--danger)', fontFamily: 'monospace' }}>
        <pre>{this.state.error.toString()}</pre>
      </div>
    );
    return this.props.children;
  }
}

// Redirects to / if not authenticated
// IMPORTANT: must wait for `loading` before redirecting — otherwise the OAuth
// callback lands on /app while user is still null (session being restored) and
// immediately bounces to /, losing the session.
function ProtectedRoute() {
  const { user, loading } = useAuth();
  if (loading) return null; // wait for session restore
  if (!user) return <Navigate to="/" replace />;
  return (
    <div className="app-container">
      <ErrorBoundary><Navbar /></ErrorBoundary>
      {/* app-main-wrapper stacks content + footer vertically */}
      <div className="app-main-wrapper">
        <main className="main-content">
          <ErrorBoundary><Outlet /></ErrorBoundary>
        </main>
        <footer className="app-main-footer">
          <Link to="/privacy">Privacy Policy</Link>
          <span aria-hidden="true">•</span>
          <Link to="/terms">Terms &amp; Conditions</Link>
        </footer>
      </div>
    </div>
  );
}

function AppRoot() {
  const [toasts, setToasts] = useState([]);
  const { user, loading } = useAuth();

  useEffect(() => {
    // Only fetch theme after auth is fully resolved and user is logged in.
    if (loading || !user) {
      applyTheme('system');
      return;
    }
    getProfiles().then(profiles => {
      const theme = profiles.length > 0 ? (profiles[0].theme || 'system') : 'system';
      applyTheme(theme);
      if (theme === 'system') {
        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        const listener = e => document.documentElement.setAttribute('data-theme', e.matches ? 'dark' : 'light');
        mq.addEventListener('change', listener);
        return () => mq.removeEventListener('change', listener);
      }
    }).catch(() => applyTheme('system'));
  }, [user, loading]);

  function applyTheme(theme) {
    if (theme === 'system') {
      const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
    } else {
      document.documentElement.setAttribute('data-theme', theme);
    }
  }

  const addToast = React.useCallback((message, type = 'success') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3500);
  }, []);

  return (
    <ToastContext.Provider value={{ addToast }}>
      <BrowserRouter>
        <Routes>
          <Route path="/"           element={<Landing />} />
          <Route path="/login"      element={<Login />} />
          <Route path="/ext-login"  element={<ExtLogin />} />
          <Route path="/privacy"    element={<Privacy />} />
          <Route path="/terms"      element={<Terms />} />
          <Route path="/app"        element={<ProtectedRoute />}>
            <Route index                   element={<Dashboard />} />
            <Route path="scope"            element={<ScopeAnalysis />} />
            <Route path="project/:id"      element={<ProjectDetail />} />
            <Route path="settings"         element={<Settings />} />
          </Route>
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </BrowserRouter>

      <div className="toast-container">
        {toasts.map(t => (
          <div key={t.id} className={`toast ${t.type}`}>{t.message}</div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppRoot />
    </AuthProvider>
  );
}
